import { z } from 'zod';
import { request } from '@/lib/api';
import { authResponseSchema } from '@/lib/schemas';

export const MFA_STATUS_KEY = ['auth', 'mfa', 'status'] as const;
export const MFA_DEVICES_KEY = ['auth', 'mfa', 'devices'] as const;

const statusSchema = z.object({
  enabled: z.boolean(),
  enabledAt: z.string().nullable(),
  recoveryCodesRemaining: z.number(),
  trustedDevices: z.number(),
  mandatory: z.boolean(),
  canDisable: z.boolean(),
});
export type MfaStatus = z.infer<typeof statusSchema>;

const deviceSchema = z.object({
  id: z.string().uuid(),
  label: z.string().nullable(),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
  expiresAt: z.string(),
});
export type TrustedDevice = z.infer<typeof deviceSchema>;

/** קוד מהאפליקציה או קוד גיבוי — השרת מקבל אחד מהשניים. */
export type SecondFactor = { code: string } | { recoveryCode: string };

export const mfaApi = {
  status: (signal?: AbortSignal) => request('/auth/mfa/status', { schema: statusSchema, signal }),
  setup: () => request('/auth/mfa/setup', { method: 'POST', schema: z.object({ secret: z.string(), otpauthUri: z.string() }) }),
  enable: (code: string) =>
    request('/auth/mfa/enable', {
      method: 'POST',
      body: { code },
      schema: z.object({ recoveryCodes: z.array(z.string()), session: authResponseSchema }),
    }),
  disable: (factor: SecondFactor) => request('/auth/mfa/disable', { method: 'POST', body: factor, schema: z.object({ enabled: z.boolean() }) }),
  regenerate: (factor: SecondFactor) =>
    request('/auth/mfa/recovery-codes', { method: 'POST', body: factor, schema: z.object({ recoveryCodes: z.array(z.string()) }) }),
  devices: (signal?: AbortSignal) => request('/auth/mfa/devices', { schema: z.array(deviceSchema), signal }),
  revokeDevice: (id: string) => request(`/auth/mfa/devices/${id}`, { method: 'DELETE' }),
  revokeAllDevices: () => request('/auth/mfa/devices', { method: 'DELETE' }),
};

/** קוד גיבוי נראה כמו "abcd-efgh"; קוד מהאפליקציה — 6 ספרות. */
export const toFactor = (value: string): SecondFactor => {
  const v = value.trim();
  return /^\d{6}$/.test(v.replace(/\s/g, '')) ? { code: v.replace(/\s/g, '') } : { recoveryCode: v };
};
